/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, switchMap } from 'rxjs';
import { faBook } from '@fortawesome/free-solid-svg-icons/faBook';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { faCommentSlash } from '@fortawesome/free-solid-svg-icons/faCommentSlash';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { AlertService } from '../../../services/alert/alert.service';
import { InterventionInstanceConfigurationInterface } from '../../../models/interface/intervention-instances/intervention-instance.configuration.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { SkillInterface } from '../../../models/interface/skill.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { HelperSkillService } from '../../../services/helper/helper-skill/helper-skill.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-instance-creation-confirmation-dialog',
  templateUrl: './instance-creation-confirmation-dialog.component.html',
  styleUrls: ['./instance-creation-confirmation-dialog.component.scss']
})
export class InstanceCreationConfirmationDialogComponent implements OnInit, OnDestroy {
  // Icon
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faComment = faComment;
  faCommentSlash = faCommentSlash;
  faHandsHelping = faHandsHelping;
  faHands = faHands;
  faBook = faBook;
  faEye = faEye;
  faSquare = faSquare;

  public studiesAndIntervention: {
    study_id: number;
    interventions: InterventionInterface[];
    collaborators: UserInterface[];
  }[] = [];

  public questionnairesIntervention: {
    intervention_id: number;
    questionnaires: LessonInterface[];
    diaries: DiaryInterface[];
    skills: SkillInterface[];
  }[] = [];

  public payloads: PayloadInterface[] = [];
  public payloadAttributes: {
    ecoach_ids: number[];
    patient_id: number;
    intervention_id: number;
    intervention_type: string;
    configuration: {
      questionnaire_configuration: InterventionInstanceConfigurationInterface[];
      custom_order: number[];
      starting_at: number;
    };
  }[] = [];

  public reminderConfigs: { payload: PayloadInterface; intervention: InterventionInterface }[] = [];

  public myUserId: number;

  public emailSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public studyMembers: UserInterface[] = [];
  public studyCollaborator: UserInterface[] = [];

  public isHiddenSubject = new BehaviorSubject<boolean>(false);
  public isHidden$: Observable<boolean>;

  public numbersOfPages: number;
  public pageSubject = new BehaviorSubject<number>(0);
  public page$: Observable<number>;

  public interventions: InterventionInterface[] = [];

  public questionnaires: LessonInterface[] = [];

  public selectedLanguageSubject = new BehaviorSubject<string>('');

  public defaultReminderText: Object;

  private subscriptions: Subscription[] = [];

  private readonly instanceKey = 'intervention-instance-reminder';
  private readonly greetingKey = 'default_reminder_message_head';
  private readonly textKey = 'default_reminder_message';
  private readonly closingKey = 'default_reminder_closing';
  private readonly signatureKey = 'default_reminder_signature';

  constructor(
    private alertService: AlertService,
    private helperService: HelperService,
    private helperSkillService: HelperSkillService,
    private helperDialogService: HelperDialogService,
    private translateService: TranslateService,
    private httpClient: HttpClient,
  ) {
    this.selectedLanguageSubject.next(this.translateService.currentLang);
    this.subscriptions.push(
      this.selectedLanguageSubject
        .pipe(switchMap((language) => this.httpClient.get(`../../../../assets/i18n/${language}.json`)))
        .subscribe((translation) => {
          this.defaultReminderText = translation;
        }),
    );
  }

  public get helper() {
    return this.helperService;
  }

  public get helperSkill() {
    return this.helperSkillService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get alert() {
    return this.alertService;
  }

  // Set members of study from parent
  @Input()
  set _members(_members: UserInterface[]) {
    this.studyMembers = _members;
  }

  // Set collaborators of study from parent
  @Input()
  set _collaborators(_collaborators: UserInterface[]) {
    if (_collaborators && _collaborators.length > 0) {
      this.studyCollaborator = _collaborators;
    }
  }

  @Input() _myUserId(_myUserId: number) {
    if (_myUserId) {
      this.myUserId = _myUserId;
    }
  }

  @Input() _email(_email: string) {
    if (_email) {
      this.emailSubject.next(_email);
    }
  }

  // Set study and its configuration from parent
  @Input() hide(_hide: boolean) {
    if (_hide !== undefined) {
      this.isHiddenSubject.next(_hide);
    }
  }

  @Input() _payloads(_payloads: PayloadInterface[]) {
    if (_payloads) {
      this.payloads = [];
      this.payloadAttributes = [];

      this.payloads = _payloads;
      this.payloads.forEach((payload: PayloadInterface) => {
        this.payloadAttributes.push(payload.data.attributes);
      });
      this.numbersOfPages = this.payloads.length;
      this.pageSubject.next(0);

      this.reminderConfigs = Array(this.numbersOfPages).fill(null);
    }
  }

  @Input() _studiesAndIntervention(
    _studiesAndIntervention: {
      study_id: number;
      interventions: InterventionInterface[];
      collaborators: UserInterface[];
    }[],
  ) {
    if (_studiesAndIntervention) {
      this.studiesAndIntervention = _studiesAndIntervention;
      if (this.studiesAndIntervention.length > 0) {
        const flattenedCollaborators = [].concat(...this.studiesAndIntervention.map(x => x.collaborators));
        this.studyCollaborator = flattenedCollaborators;

        const flattenedInterventions = [].concat(...this.studiesAndIntervention.map(x => x.interventions));
        this.interventions = flattenedInterventions;
      }
    }
  }

  @Input() _questionnairesIntervention(
    _questionnairesIntervention: {
      intervention_id: number;
      questionnaires: LessonInterface[];
      diaries: DiaryInterface[];
      skills: SkillInterface[];
    }[],
  ) {
    if (_questionnairesIntervention) {
      this.questionnairesIntervention = _questionnairesIntervention;
      const flattenedQuestionnaires = [].concat(...this.questionnairesIntervention.map(x => x.questionnaires));
      this.questionnaires = this.questionnaires.concat(flattenedQuestionnaires);
    }
  }

  ngOnInit(): void {
    // Get hidden
    this.isHidden$ = this.isHiddenSubject.asObservable();
    this.page$ = this.pageSubject.asObservable();
  }

  getReminderGreeting(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.greetingKey];
    } else {
      return '';
    }
  }

  getReminderMessage(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.textKey];
    } else {
      return '';
    }
  }

  getReminderClosing(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.closingKey];
    } else {
      return '';
    }
  }

  getReminderSignature(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.signatureKey];
    } else {
      return '';
    }
  }

  public getQuestionnaireById(questionnaireId: number): LessonInterface {
    const found = this.questionnaires.find(
      (questionnaire) => questionnaire.id.toString() === questionnaireId.toString(),
    );
    return found !== undefined ? found : null;
  }

  public getConfigurationByQuestionnaireId(questionnaireId: number, index: number): InterventionInstanceConfigurationInterface {
    const configuration = this.payloadAttributes[index].configuration.questionnaire_configuration;
    for (let i = configuration.length - 1; i >= 0; i--) {
      const config = configuration[i];
      if (config.id.toString() === questionnaireId.toString()) {
        return config;
      }
    }
    return null;
  }

  public showAfterPreviousUnlockHint(questionnaireId: number, index: number): boolean {
    const configuration = this.payloadAttributes[index].configuration;
    const foundConfig = configuration.questionnaire_configuration.find(
      (config) => config.id.toString() === questionnaireId.toString(),
    );
    if (!foundConfig || foundConfig.unlock_type !== 'after_previous') {
      return false;
    }
    // Get configurations based on custom_order
    const currentPos = configuration.custom_order.findIndex(
      (value) => value.toString() === questionnaireId.toString(),
    );
    if (currentPos < 1) {
      return false;
    }
    const previousPos = currentPos - 1;
    const previousConfigId = configuration.custom_order[previousPos].toString();
    const predecessor = configuration.questionnaire_configuration.find(
      (config) => config.id.toString() === previousConfigId,
    );
    return predecessor !== undefined && predecessor.feedback_required;
  }

  // Display previous page
  public showPrevious(): void {
    const pageNumber = this.pageSubject.getValue();
    if (pageNumber > 0) {
      this.pageSubject.next(pageNumber - 1);
    }
  }

  // Display next page
  public showNext(): void {
    const pageNumber = this.pageSubject.getValue();
    if (pageNumber < this.numbersOfPages - 1) {
      this.pageSubject.next(pageNumber + 1);
    }
  }

  public openDialogSkill(skillId: number, interventionId: number): void {
    const foundIntervention: {
      intervention_id: number;
      questionnaires: LessonInterface[];
      diaries: DiaryInterface[];
      skills: SkillInterface[];
    } = this.findInterventionSet(interventionId);
    if (foundIntervention) {
      const dialogRef = this.helperDialog.openDialogSkill(
        skillId,
        foundIntervention.skills,
        this.helper.findArrObjById(interventionId, this.interventions),
      );
      if (dialogRef) {
        dialogRef.afterClosed().subscribe((_result) => {});
      }
    }
  }

  public findInterventionSet(interventionId: number): {
    intervention_id: number;
    questionnaires: LessonInterface[];
    diaries: DiaryInterface[];
    skills: SkillInterface[];
  } {
    return this.questionnairesIntervention.find(
      (value) => value.intervention_id.toString() === interventionId.toString(),
    );
  }

  public openDialogDiaryPreview(diary: DiaryInterface) {
    this.helperDialog
      .openDialogDiaryPreview(diary)
      .afterClosed()
      .subscribe(() => {});
  }

  public findSkills(interventionId: number): SkillInterface[] {
    if (interventionId) {
      const found = this.findInterventionSet(interventionId);
      return found ? found.skills : [];
    }
    return [];
  }

  public findDiaries(interventionId: number): DiaryInterface[] {
    if (interventionId) {
      const found = this.findInterventionSet(interventionId);
      return found ? found.diaries : [];
    }
    return [];
  }

  public openDialogInterventionInstanceReminderCreation(
    index?: number,
    reminder?: {
      payload: PayloadInterface;
      intervention: InterventionInterface;
    },
  ): void {
    this.helperDialog
      .openDialogCreateInterventionInstanceReminder(undefined, undefined, reminder?.payload)
      .afterClosed()
      .subscribe((result) => {
        if (!(result instanceof HttpResponse) && !!result) {
          this.reminderConfigs[index] = {
            payload: result,
            intervention: this.helper.findArrObjById(this.payloadAttributes[index].intervention_id, this.interventions),
          };
        }
      });
  }

  public removeReminderConfig(index?: number): void {
    if (!!index || index === 0) {
      this.reminderConfigs[index] = null;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
